import { useEffect } from "react";

/**
 * Aggressive scroll reset hook to fix scroll jump bugs on building pages.
 * Automatically detects and skips scroll prevention when dialogs are open.
 */
export function useScrollReset() {
  useEffect(() => {
    let stale = false;

    // Check if any dialogs are currently open
    const isDialogOpen = () => {
      return (
        document.querySelector('[role="dialog"]') !== null ||
        document.querySelector('[data-state="open"]') !== null ||
        document.querySelector("[data-radix-dialog-content]") !== null
      );
    };

    // Force scroll loop
    function loopScrollTop(attempt = 1) {
      if (stale || isDialogOpen()) return;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (attempt < 7) {
        setTimeout(() => loopScrollTop(attempt + 1), 24 * attempt);
      }
    }
    loopScrollTop();

    // Prevent scrollIntoView from focusing elements and scrolling, but skip for dialogs
    const handleFocus = (event: FocusEvent) => {
      if (event.target && event.target instanceof HTMLElement) {
        // Skip if focus is within a dialog
        const isInDialog = event.target.closest('[role="dialog"]') !== null;
        if (!isInDialog) {
          event.target.scrollIntoView = () => {
            // Intentionally disabled to prevent unwanted scrolling
          };
        }
      }
    };
    document.addEventListener("focus", handleFocus, true);

    // Also patch scroll restoration
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    return () => {
      stale = true;
      document.removeEventListener("focus", handleFocus, true);
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "auto";
      }
    };
  }, []);
}
